import { Combobox } from '@headlessui/react';
import React from 'react';
import { debounce } from '../utils/debounce';
import { SearchboxButton } from './SearchboxButton';
import { SearchboxDialog } from './SearchboxDialog';
import { SearchboxInput } from './SearchboxInput';
import { SearchboxList } from './SearchboxList';
import { SearchboxPopover } from './SearchboxPopover';
import { useClickOutside } from './useClickOutside';

type SearchboxProps<TValue> = {
    accessibilityHeading?: () => React.ReactNode;
    children?: React.ReactNode;
    defaultValue?: TValue;
    defaultOption?: () => React.ReactNode;
    displayValue?: (item?: TValue) => string;
    isModal?: boolean;
    isStatic?: boolean;
    name?: string;
    onChange: (value: string) => void;
    onFocus?: () => void;
    onSelect: (value: TValue) => boolean;
    openOnFocus?: boolean;
    placeholder?: string;
};

function SearchboxInner<TValue>(
    props: SearchboxProps<TValue>,
    forwardedRef: React.ForwardedRef<HTMLInputElement>,
) {
    const {
        accessibilityHeading,
        children,
        defaultValue,
        defaultOption,
        displayValue,
        isModal = false,
        isStatic = false,
        name,
        onChange,
        onFocus,
        onSelect,
        openOnFocus = false,
        placeholder,
    } = props;

    // We need to keep track of the input ref to be able to blur it when the
    // modal is dismissed.
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    // Use a callback to merge refs.
    const inputRefCallback = React.useCallback(
        (node: HTMLInputElement | null) => {
            inputRef.current = node;
            if (typeof forwardedRef === 'function') {
                forwardedRef(node);
            } else if (forwardedRef) {
                forwardedRef.current = node;
            }
        },
        [forwardedRef, inputRef],
    );

    const [isFocused, setIsFocused] = React.useState(false);

    const handleFocus = React.useCallback(() => {
        onFocus && onFocus();
        openOnFocus && setIsFocused(true);
    }, [onFocus, openOnFocus, setIsFocused]);

    const handleModalDismiss = React.useCallback(() => {
        inputRef.current?.blur();
        onChange('');
        setIsFocused(false);
    }, [inputRef, onChange, setIsFocused]);

    const containerRef = useClickOutside<HTMLDivElement>(
        React.useCallback(() => {
            setIsFocused(false);
        }, [setIsFocused]),
    );

    const [debouncedOnChange] = React.useMemo(
        () => debounce(onChange, 100),
        [onChange],
    );

    const handleSelect = React.useCallback(
        (value: TValue) => {
            if (value) {
                const keepOpen = onSelect && onSelect(value);
                setIsFocused(keepOpen);
            }
        },
        [onSelect, setIsFocused],
    );

    const memoizedAccessibilityHeading = React.useMemo(() => {
        return accessibilityHeading ? accessibilityHeading() : null;
    }, [accessibilityHeading]);

    const memoizedDefaultOption = React.useMemo(() => {
        return defaultOption ? defaultOption() : null;
    }, [defaultOption]);

    return (
        <Combobox
            as="div"
            className="searchbox"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore-error TS2769: No overload matches this call.
            defaultValue={defaultValue}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore-error TS2769: No overload matches this call.
            nullable
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore-error TS2769: No overload matches this call.
            onChange={handleSelect}
            ref={containerRef}
        >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore-error TS2769: No overload matches this call. */}
            {({ open }) => {
                const isOpen = isStatic || (openOnFocus ? isFocused : open);
                const isModalOpen = isModal && isOpen;
                return (
                    <SearchboxDialog
                        isModal={isModal}
                        isOpen={isOpen}
                        onDismiss={handleModalDismiss}
                    >
                        <SearchboxInput
                            displayValue={displayValue}
                            name={name}
                            onChange={debouncedOnChange}
                            onFocus={handleFocus}
                            placeholder={placeholder}
                            ref={inputRefCallback}
                        >
                            {!isModalOpen && <SearchboxButton />}
                        </SearchboxInput>
                        <SearchboxPopover
                            isModal={isModal}
                            isOpen={isOpen}
                            isStatic={isStatic}
                        >
                            {memoizedAccessibilityHeading}
                            <SearchboxList>
                                {memoizedDefaultOption}
                                {children}
                            </SearchboxList>
                        </SearchboxPopover>
                    </SearchboxDialog>
                );
            }}
        </Combobox>
    );
}

// Redeclare React.forwardRef to add support for generic types
// https://react-typescript-cheatsheet.netlify.app/docs/basic/getting-started/forward_and_create_ref/#option-2---redeclare-forwardref
declare module 'react' {
    // eslint-disable-next-line @typescript-eslint/ban-types
    function forwardRef<T, P = {}>(
        render: (props: P, ref: React.Ref<T>) => React.ReactElement | null,
    ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

export const Searchbox = React.forwardRef(SearchboxInner);
